/**
 * Modal Context Bar Component
 * Shows active Brand/Product context at top of modals
 *
 * Used in: Image Generation, Video Generation, Image Edit modals
 * Provides consistent brand/product context display across all modals
 *
 * @version 6.0
 * - Shell color ONLY on header
 * - White body with collapsible product cartridge inside
 * - Product details (description, benefit) hidden until expanded
 * - Light grey footer with color swatches and toggles
 */

import { GeneratorState } from '../../features/generator/core/state.js';

// Context injection state (can be toggled by user)
export const ContextState = {
  useBrand: true,
  useProduct: true
};

/**
 * Generate HTML for context bar
 * Call this when building modal HTML to insert the context bar
 *
 * @param {string} idPrefix - Unique prefix for element IDs (e.g., 'image-gen', 'video-gen')
 * @returns {string} HTML string for the context bar
 */
export function getContextBarHTML(idPrefix) {
  return `
    <div class="modal-context-bar" id="${idPrefix}-context">
      <div class="context-cartridge" id="${idPrefix}-cartridge">
        <div class="context-cart-header" id="${idPrefix}-header">
          <span class="context-cart-led"></span>
          <span class="context-cart-name" id="${idPrefix}-brand-name"></span>
          <span class="context-cart-industry" id="${idPrefix}-industry"></span>
        </div>
        <div class="context-cart-body" id="${idPrefix}-body">
          <div class="context-cart-product" id="${idPrefix}-product-row">
            <div class="context-cart-product-header" id="${idPrefix}-product-header">
              <span class="context-cart-expand-icon" id="${idPrefix}-expand-icon">▶</span>
              <span class="context-cart-product-label">PRODUCT:</span>
              <span class="context-cart-product-name" id="${idPrefix}-product-name"></span>
            </div>
            <div class="context-cart-product-details" id="${idPrefix}-product-details">
              <div class="context-cart-desc" id="${idPrefix}-desc-row">
                <span class="context-cart-desc-text" id="${idPrefix}-product-desc"></span>
              </div>
              <div class="context-cart-benefit" id="${idPrefix}-benefit-row">
                <span class="context-cart-benefit-label">BENEFIT:</span>
                <span class="context-cart-benefit-text" id="${idPrefix}-benefit"></span>
              </div>
            </div>
          </div>
        </div>
        <div class="context-cart-footer">
          <div class="context-cart-colors" id="${idPrefix}-colors"></div>
          <div class="context-cart-toggles">
            <label class="context-toggle-label">
              <input type="checkbox" id="${idPrefix}-use-brand" checked>
              <span>Brand</span>
            </label>
            <label class="context-toggle-label">
              <input type="checkbox" id="${idPrefix}-use-product" checked>
              <span>Product</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  `;
}

// Shell color mapping (matches phase4-harvest.js)
const SHELL_COLORS = {
  slate: '#64748b',
  orange: '#f97316',
  charcoal: '#374151',
  violet: '#8b5cf6',
  teal: '#14b8a6',
  blue: '#3b82f6',
  rose: '#f43f5e',
  amber: '#f59e0b',
  stone: '#78716c'
};

/**
 * Update context bar with brand, product, benefit, and description
 * Shell color on header only, white body with collapsible product
 *
 * @param {string} idPrefix - Unique prefix for element IDs (must match getContextBarHTML)
 * @param {Object} [contextData] - Optional context data { brand, brief, selectedProduct }
 */
export function updateContextBar(idPrefix, contextData = null) {
  const contextBar = document.getElementById(`${idPrefix}-context`);
  const headerEl = document.getElementById(`${idPrefix}-header`);
  const bodyEl = document.getElementById(`${idPrefix}-body`);
  const brandNameEl = document.getElementById(`${idPrefix}-brand-name`);
  const industryEl = document.getElementById(`${idPrefix}-industry`);
  const productRow = document.getElementById(`${idPrefix}-product-row`);
  const productHeader = document.getElementById(`${idPrefix}-product-header`);
  const productNameEl = document.getElementById(`${idPrefix}-product-name`);
  const colorsEl = document.getElementById(`${idPrefix}-colors`);
  const benefitRow = document.getElementById(`${idPrefix}-benefit-row`);
  const benefitEl = document.getElementById(`${idPrefix}-benefit`);
  const descRow = document.getElementById(`${idPrefix}-desc-row`);
  const descEl = document.getElementById(`${idPrefix}-product-desc`);
  const useBrandCheckbox = document.getElementById(`${idPrefix}-use-brand`);
  const useProductCheckbox = document.getElementById(`${idPrefix}-use-product`);

  if (!contextBar) {
    console.warn(`[ContextBar] Element not found: ${idPrefix}-context`);
    return;
  }

  // Get brand object (same pattern as phase4-harvest.js)
  const brand = contextData?.brand || GeneratorState.sessionBrand || GeneratorState.currentBrand;

  // Get the SELECTED PRODUCT - this is the product user added to brand and picked from dropdown
  // Priority: selectedProduct (current session) > sessionProduct (reopened) > brief.productData
  const selectedProduct = contextData?.selectedProduct ||
                          GeneratorState.selectedProduct ||
                          GeneratorState.sessionProduct ||
                          contextData?.brief?.productData ||
                          GeneratorState.brief?.productData;

  // Get benefit from brief
  const benefit = contextData?.brief?.benefit || GeneratorState.brief?.benefit || null;

  // Get product name from the selected product object (NOT from brief.product which is just text)
  const productName = selectedProduct?.name || null;

  // Get product description from selected product
  const description = selectedProduct?.description || null;

  // Debug logging
  console.log(`[ContextBar] brand:`, brand?.name || 'null', `shell_color:`, brand?.shell_color || 'null');
  console.log(`[ContextBar] selectedProduct:`, selectedProduct || 'null');
  console.log(`[ContextBar] productName:`, productName || 'null');
  console.log(`[ContextBar] colors:`, brand?.colors || 'null');
  console.log(`[ContextBar] benefit:`, benefit || 'null');
  console.log(`[ContextBar] description:`, description || 'null');

  // Check if we have anything to show
  if (!brand && !productName && !benefit) {
    contextBar.style.display = 'none';
    return;
  }

  // Show the context bar
  contextBar.style.display = 'block';

  // Get shell color using mapping (same as phase4-harvest.js)
  // Check both snake_case and camelCase since different sources use different naming
  const shellColorKey = brand?.shell_color || brand?.shellColor || 'slate';
  const shellColor = brand ? (SHELL_COLORS[shellColorKey] || '#64748b') : '#64748b';
  console.log(`[ContextBar] Shell color key: ${shellColorKey}, hex: ${shellColor}`);

  // Get brand colors
  const colors = brand?.colors || brand?.primary_colors || [];

  // Set shell color on HEADER only (not entire cartridge)
  if (headerEl) {
    headerEl.style.background = shellColor;
  }

  // Set brand name (use productName as fallback if no brand)
  if (brandNameEl) {
    brandNameEl.textContent = brand?.name || productName || 'Context';
  }

  // Set industry
  if (industryEl) {
    if (brand?.industry) {
      industryEl.textContent = brand.industry;
      industryEl.style.display = 'inline';
    } else {
      industryEl.style.display = 'none';
    }
  }

  // Show product row if we have a selected product with a name
  // Product is collapsible - starts collapsed
  if (selectedProduct && productName && productRow && productNameEl) {
    productNameEl.textContent = productName;
    productRow.style.display = 'flex';
    if (bodyEl) bodyEl.style.display = 'flex';
    console.log(`[ContextBar] Showing product:`, productName);

    // Setup expand/collapse behavior on product header
    if (productHeader && !productHeader._clickBound) {
      productHeader._clickBound = true;
      productHeader.addEventListener('click', () => {
        productRow.classList.toggle('expanded');
      });
    }
  } else {
    // No product - hide body section entirely
    if (productRow) productRow.style.display = 'none';
    if (bodyEl) bodyEl.style.display = 'none';
  }

  // Show description inside product details
  if (selectedProduct && description && descRow && descEl) {
    descEl.textContent = description;
    descRow.style.display = 'block';
    console.log(`[ContextBar] Showing description:`, description.substring(0, 50));
  } else if (descRow) {
    descRow.style.display = 'none';
  }

  // Show benefit inside product details
  if (benefit && benefitRow && benefitEl) {
    benefitEl.textContent = benefit;
    benefitRow.style.display = 'flex';
  } else if (benefitRow) {
    benefitRow.style.display = 'none';
  }

  // Set color swatches
  if (colorsEl && colors.length > 0) {
    colorsEl.innerHTML = colors.slice(0, 4).map(color =>
      `<span class="context-cart-swatch" style="background: ${color}"></span>`
    ).join('');
    colorsEl.style.display = 'flex';
  } else if (colorsEl) {
    colorsEl.style.display = 'none';
  }

  // Setup toggle checkboxes
  if (useBrandCheckbox) {
    useBrandCheckbox.checked = ContextState.useBrand;
    useBrandCheckbox.onchange = () => {
      ContextState.useBrand = useBrandCheckbox.checked;
      console.log(`[ContextBar] useBrand:`, ContextState.useBrand);
    };
  }
  if (useProductCheckbox) {
    useProductCheckbox.checked = ContextState.useProduct;
    useProductCheckbox.onchange = () => {
      ContextState.useProduct = useProductCheckbox.checked;
      console.log(`[ContextBar] useProduct:`, ContextState.useProduct);
    };
  }
}

/**
 * Setup context toggle (legacy - kept for backwards compatibility)
 * @deprecated Use the checkbox toggles in the context bar instead
 */
export function setupContextToggle(idPrefix, startCollapsed = true) {
  // No-op - toggle checkboxes are now inline
}

/**
 * Get current brand/product context as an object
 * Useful for passing context to API calls
 *
 * @param {Object} [contextData] - Optional context data to use instead of GeneratorState
 * @returns {Object} Context object { brandId, brandName, productId, productName, productDescription }
 */
export function getContext(contextData = null) {
  let brand, product, productId;

  if (contextData) {
    brand = contextData.brand || contextData.sessionBrand;
    product = contextData.product || contextData.selectedProduct || contextData.sessionProduct;
    productId = contextData.brief?.productId || product?.id;
  } else {
    brand = GeneratorState.currentBrand || GeneratorState.sessionBrand;
    product = GeneratorState.selectedProduct || GeneratorState.sessionProduct;
    productId = GeneratorState.brief?.productId;
  }

  return {
    brandId: brand?.id || null,
    brandName: brand?.name || null,
    productId: productId || product?.id || null,
    productName: product?.name || null,
    productDescription: product?.description || null,
    hasBrand: !!brand,
    hasProduct: !!(product || productId)
  };
}
